import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid, Select, MenuItem, FormControl, Switch, Radio, RadioGroup, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import styles from "./style.js";
import PlanCountryPricing from "../../../components/PlanCountryPricing";
import PlanPricingEditor from "../../../components/PlanPricingEditor";
import currencyCodes from "currency-codes";
import {
  billingCycles,
  createPlanText,
  planModuleDefaultToggles,
  planModuleDefaultValues,
  planModuleRows,
} from "../../../constants/data";
import {
  validatePlanForm,
  moduleNumericFieldKeys,
  coercePositiveIntegerInput,
  coerceNonNegativeIntegerInput,
  getPlanFieldError,
} from "../../../utils/validation";

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[^a-z0-9]/g, "");

const countryCurrencyMap = currencyCodes.data.reduce((acc, item) => {
  const currencyCode = String(item?.code || "").trim().toUpperCase();
  if (!currencyCode || !Array.isArray(item?.countries)) return acc;

  item.countries.forEach((countryName) => {
    const key = normalizeText(countryName);
    if (key && !acc[key]) {
      acc[key] = currencyCode;
    }
  });

  return acc;
}, {});

const getCurrencyByCountryName = (countryName) => {
  const key = normalizeText(countryName);
  if (!key) return "";
  return countryCurrencyMap[key] || "";
};

const CreateNewPlanPage = ({ loading, fetching, handleCreatePlan, fetchPlanData, isEditMode = false, planId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const COMPANIES_MAX_LIMIT = 1000;
  const stateCountry = String(location?.state?.country || "").trim();
  const stateCurrency = String(location?.state?.currency || "").trim().toUpperCase();

  const [formData, setFormData] = useState({
    planName: "",
    billingCycle: "",
    price: "",
    country: "",
    currency: "",
    maxEmployees: "",
    payslipsPerYear: "",
    isActive: true,
    trialEnabled: false,
    trialDuration: "",
  });

  const [initialFormData, setInitialFormData] = useState(null);
  // Multi-country pricing[] built on the create screen ([{country,currency,price}]).
  const [pricingList, setPricingList] = useState([]);
  const [errors, setErrors] = useState({});

  const [moduleToggles, setModuleToggles] = useState(() => ({ ...planModuleDefaultToggles }));
  const [moduleValues, setModuleValues] = useState(() => ({ ...planModuleDefaultValues }));
  const [moduleErrors, setModuleErrors] = useState({});
  const [initialModuleToggles, setInitialModuleToggles] = useState(null);
  const [initialModuleValues, setInitialModuleValues] = useState(null);
  const [planScope, setPlanScope] = useState("perCompany");
  const [initialPlanScope, setInitialPlanScope] = useState("perCompany");

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#28AA4A",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor:
        theme.palette.mode === "dark" ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.25)",
      boxSizing: "border-box",
    },
  }));

  useEffect(() => {
    if (isEditMode && planId && fetchPlanData) {
      fetchPlanData(
        setFormData,
        setInitialFormData,
        setModuleToggles,
        setModuleValues,
        setInitialModuleToggles,
        setInitialModuleValues,
        setPlanScope,
        setInitialPlanScope
      );
    }
  }, [isEditMode, planId, fetchPlanData]);

  useEffect(() => {
    if (isEditMode) return;
    const countryFromState = stateCountry;
    if (!countryFromState) return;
    const resolvedCurrencyFromCountry = getCurrencyByCountryName(countryFromState);
    const resolvedCurrency = resolvedCurrencyFromCountry || stateCurrency;

    setFormData((prev) => ({
      ...prev,
      country: prev.country || countryFromState,
      currency: resolvedCurrency || prev.currency || "",
    }));
  }, [isEditMode, stateCountry, stateCurrency]);

  const handleChange = (field, value) => {
    const numericFields = ["maxEmployees", "payslipsPerYear", "trialDuration"];
    let nextValue = value;
    if (field === "price") {
      nextValue = coerceNonNegativeIntegerInput(value);
    } else if (numericFields.includes(field)) {
      nextValue = coercePositiveIntegerInput(value);
    } else {
      nextValue = value;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    let fieldError = getPlanFieldError(field, nextValue);
    if (field === "trialDuration" && formData.trialEnabled) {
      if (!nextValue) {
        fieldError = "Trial Duration is required";
      }
    }
    setErrors((prev) => ({
      ...prev,
      [field]: fieldError,
    }));
  };

  const handleModuleToggle = (field, value) => {
    setModuleToggles((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "companies") {
      if (!value) {
        setModuleErrors((prev) => ({
          ...prev,
          companies: "",
        }));
        return;
      }

      const companiesValue = String(moduleValues.companies || "").trim();
      const companiesNumber = Number(companiesValue);
      const companiesError =
        companiesValue === ""
          ? "Companies is required"
          : !Number.isFinite(companiesNumber) || companiesNumber <= 0
            ? "Companies must be greater than 0"
            : companiesNumber > COMPANIES_MAX_LIMIT
              ? `Companies must be less than or equal to ${COMPANIES_MAX_LIMIT}`
              : "";

      setModuleErrors((prev) => ({
        ...prev,
        companies: companiesError,
      }));
    }

    if (!value && field === "manageEmployees") {
      setErrors((prev) => ({
        ...prev,
        maxEmployees: "",
      }));
    }

    if (!value && field === "salaryManagement") {
      setErrors((prev) => ({
        ...prev,
        payslipsPerYear: "",
      }));
    }
  };

  const handleModuleValueChange = (field, value) => {
    let nextValue = moduleNumericFieldKeys.includes(field)
      ? coercePositiveIntegerInput(value)
      : value;

    if (field === "companies" && nextValue !== "") {
      const asNumber = Number(nextValue);
      if (Number.isFinite(asNumber) && asNumber > COMPANIES_MAX_LIMIT) {
        nextValue = String(COMPANIES_MAX_LIMIT);
      }
    }

    setModuleValues((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    if (field === "companies") {
      const companiesNumber = Number(nextValue);
      const isCompaniesEnabled = !!moduleToggles.companies;
      const companiesError = !isCompaniesEnabled
        ? ""
        : nextValue === ""
          ? "Companies is required"
          : !Number.isFinite(companiesNumber) || companiesNumber <= 0
            ? "Companies must be greater than 0"
            : companiesNumber > COMPANIES_MAX_LIMIT
              ? `Companies must be less than or equal to ${COMPANIES_MAX_LIMIT}`
              : "";

      setModuleErrors((prev) => ({
        ...prev,
        companies: companiesError,
      }));
    }
  };

  const isFormValid = () => {
    const isMaxEmployeesRequired = !!moduleToggles.manageEmployees;
    const isPayslipsRequired = !!moduleToggles.salaryManagement;

    return (
      formData.planName.trim() !== "" &&
      formData.billingCycle !== "" &&
      // On create, at least one country price is required (pricing[] editor). In
      // edit mode, pricing is managed live in the Country Pricing section.
      (isEditMode || pricingList.length > 0) &&
      (!isMaxEmployeesRequired || formData.maxEmployees.trim() !== "") &&
      (!isPayslipsRequired || formData.payslipsPerYear.trim() !== "") &&
      (!formData.trialEnabled || formData.trialDuration.trim() !== "")
    );
  };

  const hasFormChanged = () => {
    if (!isEditMode || !initialFormData) {
      return true;
    }

    const basicChanged = [
      formData.planName !== initialFormData.planName,
      formData.billingCycle !== initialFormData.billingCycle,
      formData.price !== initialFormData.price,
      formData.country !== initialFormData.country,
      formData.currency !== initialFormData.currency,
      formData.maxEmployees !== initialFormData.maxEmployees,
      formData.payslipsPerYear !== initialFormData.payslipsPerYear,
      formData.trialEnabled !== initialFormData.trialEnabled,
      formData.trialDuration !== initialFormData.trialDuration,
    ].some(Boolean);

    const togglesChanged = initialModuleToggles
      ? Object.keys(initialModuleToggles).some((key) => moduleToggles[key] !== initialModuleToggles[key])
      : false;

    const normalizeValue = (value) => (value === null || value === undefined ? "" : value);
    const valuesChanged = initialModuleValues
      ? Object.keys(initialModuleValues).some(
        (key) => String(normalizeValue(moduleValues[key])) !== String(normalizeValue(initialModuleValues[key]))
      )
      : false;

    const planScopeChanged = planScope !== initialPlanScope;

    return basicChanged || togglesChanged || valuesChanged || planScopeChanged;
  };

  const validateForm = () => {
    const newErrors = validatePlanForm(formData);
    // Price/country/currency are no longer flat form fields — pricing[] is
    // managed by the pricing editor (create) / Country Pricing section (edit).
    delete newErrors.price;
    delete newErrors.country;
    delete newErrors.currency;
    if (!moduleToggles.manageEmployees) {
      delete newErrors.maxEmployees;
    }
    if (!moduleToggles.salaryManagement) {
      delete newErrors.payslipsPerYear;
    }
    if (formData.trialEnabled && !formData.trialDuration) {
      newErrors.trialDuration = "Trial Duration is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateModuleFields = () => {
    const moduleValidationErrors = {};
    const companiesValue = String(moduleValues.companies || "").trim();
    const companiesNumber = Number(companiesValue);

    if (moduleToggles.companies) {
      if (companiesValue === "") {
        moduleValidationErrors.companies = "Companies is required";
      } else if (!Number.isFinite(companiesNumber) || companiesNumber <= 0) {
        moduleValidationErrors.companies = "Companies must be greater than 0";
      } else if (companiesNumber > COMPANIES_MAX_LIMIT) {
        moduleValidationErrors.companies = `Companies must be less than or equal to ${COMPANIES_MAX_LIMIT}`;
      }
    }

    setModuleErrors(moduleValidationErrors);
    return Object.keys(moduleValidationErrors).length === 0;
  };

  const isModuleSectionValid = () => {
    if (!moduleToggles.companies) return true;
    const companiesValue = String(moduleValues.companies || "").trim();
    const companiesNumber = Number(companiesValue);
    return (
      companiesValue !== "" &&
      Number.isFinite(companiesNumber) &&
      companiesNumber > 0 &&
      companiesNumber <= COMPANIES_MAX_LIMIT
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && validateModuleFields()) {
      await handleCreatePlan(formData, moduleToggles, moduleValues, planScope, pricingList);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>
          {isEditMode ? createPlanText.titles.edit : createPlanText.titles.create}
        </Typography>
        <Typography sx={styles.subtitle}>{createPlanText.subtitle}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>{createPlanText.sections.basic}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={createPlanText.placeholders.planName}
                value={formData.planName}
                onChange={(e) => handleChange("planName", e.target.value)}
                error={!!errors.planName}
                helperText={errors.planName}
                sx={styles.input}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.billingCycle}>
                <Select
                  value={formData.billingCycle}
                  onChange={(e) => handleChange("billingCycle", e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return createPlanText.placeholders.billingCycle;
                    const match = billingCycles.find((cycle) => cycle.value === selected);
                    return match && match.label ? match.label : selected;
                  }}
                  sx={{
                    ...styles.select,
                    color: formData.billingCycle ? "inherit" : "#9E9E9E",
                    "& .MuiSelect-icon": {
                      color: formData.billingCycle ? "inherit" : "#9E9E9E",
                    },
                  }}
                >
                  <MenuItem value="" disabled>{createPlanText.placeholders.billingCycle}</MenuItem>
                  {billingCycles.map((cycle, index) => (
                    <MenuItem key={index} value={cycle.value}>
                      {cycle.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.billingCycle && (
                  <Typography sx={styles.errorText}>{errors.billingCycle}</Typography>
                )}
              </FormControl>
            </Grid>
            {/* New model: no base price. On create, assign the plan to multiple
                countries with their local prices. In edit mode, per-country
                pricing is managed live (add/edit/remove) against the plan. */}
            <Grid item xs={12}>
              {isEditMode ? (
                <PlanCountryPricing planId={planId} />
              ) : (
                <PlanPricingEditor value={pricingList} onChange={setPricingList} />
              )}
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <AntSwitch
                    checked={!!formData.trialEnabled}
                    onChange={(e) => handleChange("trialEnabled", e.target.checked)}
                    inputProps={{ "aria-label": "Trial Enabled toggle" }}
                  />
                }
                label={<Typography sx={styles.moduleLabel}>Trial Enabled</Typography>}
                sx={{ m: 0, gap: 2 }}
              />
            </Grid>
            {formData.trialEnabled && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="Trial Duration (Days)"
                  value={formData.trialDuration}
                  onChange={(e) => handleChange("trialDuration", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    min: "1",
                  }}
                  error={!!errors.trialDuration}
                  helperText={errors.trialDuration || ""}
                  sx={styles.input}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={styles.section}>
          <Box sx={styles.modulesHeader}>
            <Typography sx={{ ...styles.sectionTitle, mb: 0 }}>{createPlanText.sections.modules}</Typography>
          </Box>
          <Box>
            {planModuleRows.map((row) => (
              <Box key={row.id} sx={styles.moduleRow}>
                <Typography sx={styles.moduleLabel}>{row.label}</Typography>
                <Box sx={styles.moduleSwitch}>
                  {!row.hideToggle ? (
                    <AntSwitch
                      checked={!!moduleToggles[row.id]}
                      onChange={(e) => handleModuleToggle(row.id, e.target.checked)}
                      inputProps={{ "aria-label": `${row.label} toggle` }}
                    />
                  ) : (
                    <Box sx={styles.moduleSwitchPlaceholder} />
                  )}
                </Box>
                <Grid container spacing={2} sx={styles.moduleFields}>
                  {row.id === "manageEmployees" && moduleToggles.manageEmployees ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <Box sx={styles.radioGroupFull}>
                          <RadioGroup
                            row
                            value={planScope}
                            onChange={(e) => setPlanScope(e.target.value)}
                            sx={styles.radioGroup}
                          >
                            <FormControlLabel
                              value="perCompany"
                              control={<Radio size="small" sx={styles.radio} />}
                              label={<Typography sx={styles.radioLabel}>Per Company</Typography>}
                            />
                            <FormControlLabel
                              value="allCompanies"
                              control={<Radio size="small" sx={styles.radio} />}
                              label={<Typography sx={styles.radioLabel}>All Companies</Typography>}
                            />
                          </RadioGroup>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="text"
                          placeholder="No of Users"
                          value={formData.maxEmployees}
                          onChange={(e) => handleChange("maxEmployees", e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "." || e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                              e.preventDefault();
                            }
                          }}
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: "1",
                          }}
                          error={!!errors.maxEmployees}
                          helperText={errors.maxEmployees || ""}
                          sx={styles.input}
                        />
                      </Grid>
                    </>
                  ) : (
                    row.fields.map((field) => {
                      const isBoundToForm = !!field.boundToForm;
                      const value = isBoundToForm
                        ? formData[field.boundToForm]
                        : moduleValues[field.key];
                      const fieldError = isBoundToForm ? errors[field.boundToForm] : moduleErrors[field.key] || "";
                      const gridSize = row.fields.length > 1 ? 6 : 12;

                      if (field.type === "select") {
                        const isMultiple = !!field.multiple;
                        const hasValue = isMultiple
                          ? Array.isArray(value) && value.length > 0
                          : !!value;

                        return (
                          <Grid item xs={12} md={gridSize} key={field.key}>
                            <FormControl fullWidth>
                              <Select
                                multiple={isMultiple}
                                value={value || (isMultiple ? [] : "")}
                                onChange={(e) => handleModuleValueChange(field.key, e.target.value)}
                                displayEmpty
                                renderValue={(selected) => {
                                  if (isMultiple) {
                                    if (!Array.isArray(selected) || selected.length === 0) {
                                      return field.placeholder;
                                    }
                                    return selected.join(", ");
                                  }
                                  return selected ? selected : field.placeholder;
                                }}
                                sx={{
                                  ...styles.select,
                                  color: hasValue ? "inherit" : "#9E9E9E",
                                  "& .MuiSelect-icon": {
                                    color: hasValue ? "inherit" : "#9E9E9E",
                                  },
                                }}
                              >
                                {!isMultiple && (
                                  <MenuItem value="" disabled>
                                    {field.placeholder}
                                  </MenuItem>
                                )}
                                {(field.options || []).map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        );
                      }

                      return (
                        <Grid item xs={12} md={gridSize} key={field.key}>
                          <TextField
                            fullWidth
                            type="text"
                            placeholder={field.placeholder}
                            value={value}
                            onChange={(e) => {
                              if (isBoundToForm) {
                                handleChange(field.boundToForm, e.target.value);
                              } else {
                                handleModuleValueChange(field.key, e.target.value);
                              }
                            }}
                            onKeyDown={(e) => {
                              const isNumericField = isBoundToForm || moduleNumericFieldKeys.includes(field.key);
                              if (
                                isNumericField &&
                                (e.key === "." || e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+")
                              ) {
                                e.preventDefault();
                              }
                            }}
                            inputProps={
                              isBoundToForm || moduleNumericFieldKeys.includes(field.key)
                                ? {
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  min: "1",
                                  ...(field.key === "companies" ? { max: COMPANIES_MAX_LIMIT } : {}),
                                }
                                : undefined
                            }
                            error={!!fieldError}
                            helperText={fieldError || ""}
                            sx={styles.input}
                          />
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={styles.buttonContainer}>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={loading || fetching}
            disabled={!isFormValid() || !isModuleSectionValid() || fetching || (isEditMode && !hasFormChanged())}
            sx={styles.submitButton}
          >
            {isEditMode ? createPlanText.buttons.update : createPlanText.buttons.create}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={() => navigate("/dashboard/planManagement")}
            sx={styles.cancelButton}
          >
            {createPlanText.buttons.cancel}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateNewPlanPage;
